import { rowCategory, subcategoryLabel, attributeFieldsFor } from "@/lib/marketplace/categories";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { buildProductLd } from "@/lib/product-schema";
import { resolvePublicSellerName } from "@/lib/public-name";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Gavel, Heart, Loader2, Zap, Lock, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ensurePendingOrderForAuction } from "@/lib/order-backfill.functions";
import { startBuyNowPurchase } from "@/lib/buy-now.functions";
import { setOrderShipping, createCheckoutSession } from "@/lib/stripe-checkout.functions";
import { SHIPPING_OPTIONS, AVAILABLE_SHIPPING_OPTIONS } from "@/lib/shipping";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Countdown } from "@/components/countdown";
import { formatGBP, CONDITION_LABEL } from "@/lib/format";
import { Price, CurrencyDisclaimer } from "@/lib/currency/price";
import { useCurrency } from "@/lib/currency/use-currency";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { VerifiedSellerBadge } from "@/components/verified-seller-badge";
import { SellerBadgeStack } from "@/components/seller-badge-stack";
import { SellerHoverCard } from "@/components/seller-hover-card";
import { SellerRatingInline } from "@/components/seller-rating-inline";
import { fetchPublicSeller } from "@/lib/public-sellers";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { PromoteListingDialog } from "@/components/promote-listing-dialog";
import { Sparkles, ShieldCheck } from "lucide-react";
import { GradingCertLookup } from "@/components/grading-cert-lookup";
import { GradingCertBadge } from "@/components/auction-detail/grading-cert-badge";
import { RegistrySection } from "@/components/auction-detail/registry-section";

import { isApiGrader, type ApiGrader } from "@/lib/grading/companies";
import { MediaGallery } from "@/components/auction-detail/media-gallery";
import { TrustStrip } from "@/components/auction-detail/trust-strip";
import { AuctionCertificateBadge } from "@/components/auction-detail/certificate-badge";
import { ShippingSummary } from "@/components/auction-detail/shipping-summary";

import { BidConfirmDialog } from "@/components/auction-detail/bid-confirm-dialog";
import { AutoExtendBadge } from "@/components/auction-detail/auto-extend-badge";
import { useAutoExtendSettings, resolveAuctionAutoExtend } from "@/lib/auto-extend";
import { serverNow } from "@/lib/server-time";
import { MobileBidBar } from "@/components/auction-detail/mobile-bid-bar";
import { BidStatusBanner } from "@/components/auction-detail/bid-status-banner";
import { BidHistory } from "@/components/auction-detail/bid-history";
import { ActivitySummary } from "@/components/auction-detail/activity-summary";
import { AuctionFaq } from "@/components/auction-detail/auction-faq";
import { StatusBadge } from "@/components/status-badge";
import { GradeBadge } from "@/components/grade-badge";
import { SellerOtherAuctions } from "@/components/auction-detail/similar-auctions";
import { useRegisterRecentlyViewed } from "@/components/auction-detail/recently-viewed";
import { getAuctionInspection } from "@/lib/inspection/inspection.functions";
import { lazy, Suspense } from "react";

// Heavy, interaction-only modules: keep them out of the initial route chunk.
const InspectionPanel = lazy(() => import("@/components/auction-detail/inspection-panel"));
import type { ScorableAuction } from "@/lib/recommendations";

// Below-the-fold recommendation engine widgets (Workstream C) — lazily
// loaded so they never block the initial auction render.
const SimilarAuctionsWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.SimilarAuctionsWidget })),
);
const RelatedAuctionsWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.RelatedAuctionsWidget })),
);
const RecentlyViewedRecoWidget = lazy(() =>
  import("@/components/recommendations").then((m) => ({ default: m.RecentlyViewedWidget })),
);
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";
import { formatLanguageBadge, labelForTcgGame, labelForRarity, TCG_GAME_LABEL, RARITY_LABEL } from "@/lib/grading/companies";


type AuctionSeo = {
  id: string;
  title: string;
  description: string | null;
  images: string[] | null;
  current_bid_pence: number | null;
  starting_price_pence: number | null;
  end_time: string | null;
  grading_company: string | null;
  grade: string | null;
  status: string | null;
  created_at: string | null;
  start_time: string | null;
  condition: string | null;
  brand: string | null;
} | null;

export const Route = createFileRoute("/auctions/$id")({
  loader: async ({ params, context }): Promise<{ auction: AuctionSeo }> => {
    try {
      // Fetch the full row so we can (a) build the canonical slug redirect
      // and (b) prime the React Query cache used by AuctionDetailInner,
      // eliminating the duplicate client-side `select=*` request.
      const { data } = await supabase
        .from("auctions")
        .select("*")
        .eq("id", params.id)
        .maybeSingle();
      if (data && (data as { id: string; title: string }).title) {
        try {
          context.queryClient.setQueryData(["auction", params.id], data);
        } catch { /* ignore */ }
        const slug = buildAuctionSlug(data as never);
        throw redirect({ to: "/auction/$slug", params: { slug }, replace: true });
      }
      return { auction: (data as AuctionSeo) ?? null };
    } catch (e) {
      if (e && typeof e === "object" && "isRedirect" in e) throw e;
      return { auction: null };
    }
  },
  head: ({ params, loaderData }) => {
    const url = `https://bidnovaauctions.uk/auctions/${params.id}`;
    const a = loaderData?.auction;
    if (!a) {
      return {
        meta: [
          { title: "Auction — BidNova Auctions" },
          { name: "description", content: "Graded Pokémon card auction on BidNova Auctions." },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const grade = [a.grading_company, a.grade].filter(Boolean).join(" ");
    const title = `${a.title}${grade ? ` (${grade})` : ""} — BidNova Auctions`;
    const desc =
      (a.description?.trim() ||
        `Bid on ${a.title}${grade ? ` graded ${grade}` : ""} in GBP on BidNova Auctions.`).replace(/\s+/g, " ").slice(0, 155);
    const image = a.images?.[0];
    const price = ((a.current_bid_pence ?? a.starting_price_pence ?? 0) / 100).toFixed(2);
    const availability =
      a.status === "active"
        ? "https://schema.org/InStock"
        : "https://schema.org/SoldOut";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        ...(image
          ? [
              { property: "og:image", content: image } as const,
              { name: "twitter:image", content: image } as const,
            ]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...(image
          ? [{ rel: "preload", as: "image", href: image, fetchpriority: "high" } as const]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildProductLd({
              url,
              name: a.title,
              description: desc,
              image,
              category: "Trading Cards",
              brand: a.brand,
              sku: a.id,
              validFrom: a.created_at ?? a.start_time ?? undefined,
              priceValidUntil: a.end_time ?? undefined,
              price,
              availability,
              itemCondition:
                a.condition === "factory_sealed" || a.condition === "brand_new"
                  ? "https://schema.org/NewCondition"
                  : "https://schema.org/UsedCondition",
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://bidnovaauctions.uk/" },
              { "@type": "ListItem", position: 2, name: "Auctions", item: "https://bidnovaauctions.uk/auctions" },
              { "@type": "ListItem", position: 3, name: a.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AuctionDetailPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

async function fetchAuction(id: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) await supabase.rpc("close_expired_auctions");
  } catch { /* ignore */ }
  const { data, error } = await supabase.from("auctions").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}
async function fetchBids(id: string) {
  const { data, error } = await supabase
    .from("bids")
    .select("id,amount_pence,created_at,bidder_id")
    .eq("auction_id", id)
    .order("created_at", { ascending: false })
    // Only used for leader / outbid detection — the public history panel
    // pages through get_auction_bid_history instead.
    .limit(20);
  if (error) throw error;
  return data ?? [];
}

function auctionToScorable(a: {
  id: string;
  card_set?: string | null;
  grading_company?: string | null;
  grade?: string | null;
  trading_card_game?: string | null;
  title?: string | null;
  current_bid_pence?: number | null;
  starting_price_pence?: number | null;
  view_count?: number | null;
  bid_count?: number | null;
  created_at?: string | null;
  seller_id?: string | null;
}): ScorableAuction {
  return {
    id: a.id,
    card_set: a.card_set ?? null,
    grading_company: a.grading_company ?? null,
    grade: a.grade ?? null,
    trading_card_game: a.trading_card_game ?? null,
    title: a.title ?? null,
    price_pence: a.current_bid_pence ?? a.starting_price_pence ?? 0,
    view_count: a.view_count ?? 0,
    bid_count: a.bid_count ?? 0,
    created_at: a.created_at ?? null,
    seller_id: a.seller_id ?? null,
  };
}

function AuctionDetailPage() {
  const { id } = Route.useParams();
  return <AuctionDetailInner id={id} />;
}

export function AuctionDetailInner({
  id,
  initialAuction,
}: {
  id: string;
  /** Row the server loader already fetched; keeps the first client render identical to SSR. */
  initialAuction?: Record<string, unknown> | null;
}) {
  const { t: tb } = useTranslation("bidding");
  const { isGBP } = useCurrency();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [bidAmount, setBidAmount] = useState("");
  const [maxBidAmount, setMaxBidAmount] = useState("");
  const [buyNowOpen, setBuyNowOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buyNowShipping, setBuyNowShipping] = useState<string>(AVAILABLE_SHIPPING_OPTIONS[0].id);
  const bidFormRef = useRef<HTMLFormElement>(null);
  const prevTopBidderRef = useRef<string | null>(null);

  const { data: auction, isLoading } = useQuery({
    queryKey: ["auction", id],
    queryFn: () => fetchAuction(id),
    // The router does not dehydrate the React Query cache, so seed it from
    // loader data to avoid a server/client hydration mismatch.
    initialData: (initialAuction ?? undefined) as Awaited<ReturnType<typeof fetchAuction>> | undefined,
    refetchInterval: 15000,
    // Loader primes this cache via queryClient.setQueryData(["auction", id]).
    // Give it a short staleTime so the first client render doesn't immediately
    // refetch the same row after hydration.
    staleTime: 10_000,
  });
  const { data: bids } = useQuery({
    queryKey: ["auction-bids", id],
    queryFn: () => fetchBids(id),
    refetchInterval: 15000,
  });
  const { data: seller } = useQuery({
    queryKey: ["public-seller", auction?.seller_id],
    enabled: !!auction?.seller_id,
    queryFn: () => fetchPublicSeller(auction!.seller_id),
    staleTime: 60_000,
  });
  const fetchInspection = useServerFn(getAuctionInspection);
  const { data: inspection } = useQuery({
    queryKey: ["auction-inspection", id],
    staleTime: 5 * 60_000,
    queryFn: () => fetchInspection({ data: { auctionId: id } }),
  });

  const { data: watched } = useQuery({
    queryKey: ["watched", id, user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("watchlist")
        .select("id")
        .eq("user_id", user!.id)
        .eq("auction_id", id)
        .maybeSingle();
      return !!data;
    },
  });

  // Effective auto-extend config: per-auction override wins over global default.
  const { data: autoExtendGlobal } = useAutoExtendSettings();
  const autoExtend = resolveAuctionAutoExtend(
    auction as {
      auto_extend_enabled?: boolean | null;
      auto_extend_trigger_seconds?: number | null;
      auto_extend_seconds?: number | null;
    } | null,
    autoExtendGlobal,
  );

  // realtime subscribe (single channel per auction; polling fallback if the
  // socket drops so bid / end-time state can never go stale silently).
  useEffect(() => {
    const refetchAuction = () => qc.invalidateQueries({ queryKey: ["auction", id] });
    const refetchBids = () => {
      qc.invalidateQueries({ queryKey: ["auction-bids", id] });
      qc.invalidateQueries({ queryKey: ["auction-bid-history", id] });
    };
    let poll: number | undefined;
    const startPolling = () => {
      if (poll) return;
      poll = window.setInterval(() => {
        refetchAuction();
        refetchBids();
      }, 15_000);
    };
    const stopPolling = () => {
      if (poll) window.clearInterval(poll);
      poll = undefined;
    };

    const ch = supabase
      .channel(`auction-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "auctions", filter: `id=eq.${id}` }, refetchAuction)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "bids", filter: `auction_id=eq.${id}` }, () => {
        refetchBids();
        refetchAuction();
      })
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "auction_extensions", filter: `auction_id=eq.${id}` },
        refetchAuction,
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") stopPolling();
        else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") startPolling();
      });
    return () => {
      stopPolling();
      supabase.removeChannel(ch);
    };
  }, [id, qc]);

  const idempotencyRef = useRef<string | null>(null);
  const bidMutation = useMutation({
    mutationFn: async ({ pounds, maxPounds }: { pounds: string; maxPounds: string }) => {
      const p = Number(pounds);
      if (!Number.isFinite(p) || p <= 0) throw new Error("Enter a valid amount");
      const amountPence = Math.round(p * 100);
      let maxPence: number | null = null;
      const trimmedMax = maxPounds.trim();
      if (trimmedMax.length > 0) {
        const m = Number(trimmedMax);
        if (!Number.isFinite(m) || m <= 0) throw new Error("Enter a valid max bid");
        maxPence = Math.round(m * 100);
        if (maxPence < amountPence) throw new Error("Max bid must be at least your bid");
      }
      // Idempotency key: an accidental double-submit of the same amount
      // returns the original bid instead of placing a second one.
      const idempotencyKey =
        idempotencyRef.current ??
        (idempotencyRef.current =
          (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`) + `:${amountPence}`);
      const { data, error } = await supabase.rpc("place_bid", {
        _auction_id: id,
        _amount_pence: amountPence,
        ...(maxPence !== null ? { _max_amount_pence: maxPence } : {}),
        _idempotency_key: idempotencyKey,
      } as never);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      idempotencyRef.current = null;
      const result = data as
        | { extended?: boolean; auto_countered?: boolean; extension_seconds?: number; duplicate?: boolean }
        | null;
      toast.success(
        result?.duplicate
          ? "Bid already placed"
          : result?.extended
          ? `Bid placed — auction extended by ${result.extension_seconds ?? autoExtend.extendSeconds} seconds ⏱️`
          : result?.auto_countered
          ? "Bid placed — proxy bidding applied"
          : "Bid placed",
      );
      setBidAmount("");
      setMaxBidAmount("");
      setConfirmOpen(false);
      track("bid_success", { auction_id: id });
      qc.invalidateQueries({ queryKey: ["auction", id] });
      qc.invalidateQueries({ queryKey: ["auction-bids", id] });
      qc.invalidateQueries({ queryKey: ["auction-bid-history", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Detect end_time extensions from realtime updates and notify all viewers.
  const lastEndRef = useRef<string | null>(null);
  useEffect(() => {
    if (!auction?.end_time) return;
    const prev = lastEndRef.current;
    if (prev && new Date(auction.end_time).getTime() > new Date(prev).getTime() + 1000) {
      toast(`⏱️ Auction extended by ${autoExtend.extendSeconds} seconds due to a late bid.`, {
        duration: 6000,
      });
    }
    lastEndRef.current = auction.end_time;
  }, [auction?.end_time, autoExtend.extendSeconds]);

  // When the clock runs out, pull the authoritative final status (sold /
  // reserve_not_met / ended) instead of leaving a stale "active" view.
  useEffect(() => {
    if (!auction?.end_time || auction.status !== "active") return;
    const ms = new Date(auction.end_time).getTime() - serverNow();
    if (ms > 6 * 60 * 60 * 1000) return; // only arm near the end
    const t = window.setTimeout(
      () => {
        qc.invalidateQueries({ queryKey: ["auction", id] });
        window.setTimeout(() => qc.invalidateQueries({ queryKey: ["auction", id] }), 8000);
      },
      Math.max(0, ms) + 1500,
    );
    return () => window.clearTimeout(t);
  }, [auction?.end_time, auction?.status, id, qc]);



  const watchMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in to watch");
      if (watched) {
        await supabase.from("watchlist").delete().eq("user_id", user.id).eq("auction_id", id);
      } else {
        await supabase.from("watchlist").insert({ user_id: user.id, auction_id: id });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watched", id, user?.id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  // Winner-only: fetch the pending_payment order (if any) for this auction so
  // we can show a "Pay now" button on the ended auction detail page. RLS
  // scopes orders to buyer/seller/admin, so a non-winner just sees nothing.
  const isEndedForOrder =
    !!auction && (auction.status !== "active" || new Date(auction.end_time) <= new Date());
  const isWinnerForOrder = !!user && auction?.winner_id === user.id;
  const { data: winnerOrder, isLoading: winnerOrderLoading } = useQuery({
    queryKey: ["auction-order", id, user?.id],
    enabled: !!user && isEndedForOrder && isWinnerForOrder,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id,status,has_active_dispute,buyer_id")
        .eq("auction_id", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    refetchInterval: 10000,
  });

  const ensureOrderFn = useServerFn(ensurePendingOrderForAuction);
  const payNow = useMutation({
    mutationFn: async () => {
      // Backfill/verify the order server-side (never trust client amounts).
      await ensureOrderFn({ data: { auctionId: id } });
      qc.invalidateQueries({ queryKey: ["auction-order", id, user?.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      // Send the buyer to Orders where they pick shipping and are redirected
      // to Stripe Checkout by the existing server-side createCheckoutSession.
      navigate({ to: "/orders" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Buy It Now: reserve + shipping + Stripe checkout, all server-authoritative.
  const startBinFn = useServerFn(startBuyNowPurchase);
  const setShippingFn = useServerFn(setOrderShipping);
  const createSessionFn = useServerFn(createCheckoutSession);
  const buyNowMutation = useMutation({
    mutationFn: async () => {
      const shipOpt = SHIPPING_OPTIONS.find((o) => o.id === buyNowShipping);
      if (!shipOpt) throw new Error("Choose a shipping method");
      const res = await startBinFn({ data: { auctionId: id } });
      await setShippingFn({ data: { orderId: res.order_id, shippingMethod: shipOpt.id as never } });
      const session = await createSessionFn({
        data: {
          orderId: res.order_id,
          origin: window.location.origin,
          shippingMethod: shipOpt.id as never,
        },
      });
      if (!session.url) throw new Error("Could not start checkout");
      window.location.href = session.url;
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setBuyNowOpen(false);
      qc.invalidateQueries({ queryKey: ["auction", id] });
    },
  });

  // Register this listing in localStorage for the "Recently viewed" section.
  useRegisterRecentlyViewed(
    auction
      ? {
          id: auction.id,
          title: auction.title,
          image: auction.images?.[0] ?? null,
          grading_company: auction.grading_company ?? null,
          grade: auction.grade ?? null,
          savedAt: Date.now(),
        }
      : null,
  );

  // Fire an "auction_view" event once per listing view.
  const trackedViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!auction || trackedViewRef.current === auction.id) return;
    trackedViewRef.current = auction.id;
    track("auction_view", { auction_id: auction.id });
  }, [auction]);

  // Outbid detection: only toast when the previous top-bidder was the current user
  // and a newer bid has moved them off the top.
  useEffect(() => {
    if (!bids || bids.length === 0 || !user) return;
    const topBidder = bids[0].bidder_id;
    const prevTop = prevTopBidderRef.current;
    prevTopBidderRef.current = topBidder;
    if (prevTop && prevTop === user.id && topBidder !== user.id) {
      toast.warning("You've been outbid — place a new bid to stay in the lead", {
        action: {
          label: "Bid again",
          onClick: () => bidFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
        },
      });
    }
  }, [bids, user]);

  // Reserve the full auction-body height on the pre-data render so the
  // footer doesn't paint mid-viewport and then get shoved off-screen when
  // the real content mounts (root cause of the ~0.6 CLS score).
  if (isLoading) return (
    <div className="mx-auto max-w-6xl px-4 py-16 min-h-[1600px] lg:min-h-[1200px]">
      <Loader2 className="h-6 w-6 animate-spin text-gold" />
    </div>
  );
  if (!auction) return (
    <div className="mx-auto max-w-6xl px-4 py-16 min-h-[80svh] text-center text-muted-foreground">Auction not found.</div>
  );

  const currentBid = auction.current_bid_pence ?? auction.starting_price_pence;
  
  const minNext = (auction.current_bid_pence ?? 0) > 0
    ? (auction.current_bid_pence ?? 0) + auction.bid_increment_pence
    : auction.starting_price_pence;
  const isSeller = user?.id === auction.seller_id;
  const isEnded = auction.status !== "active" || new Date(auction.end_time) <= new Date();
  const isWinner = auction.winner_id === user?.id;
  const reservationActive =
    !!auction.reservation_expires_at &&
    new Date(auction.reservation_expires_at) > new Date();
  const reservedByMe = reservationActive && auction.reserved_by_user_id === user?.id;
  const reservedByOther = reservationActive && !reservedByMe;
  const hasBidsAlready = (auction.bid_count ?? 0) > 0;
  const binDisabledByBidding =
    !!auction.buy_now_enabled &&
    hasBidsAlready &&
    !auction.keep_buy_now_after_bid;
  const buyNowAvailable =
    !!auction.buy_now_enabled &&
    !!auction.buy_now_price_pence &&
    auction.status === "active" &&
    !isEnded &&
    !binDisabledByBidding &&
    !reservedByOther;

  const isCardListing = !!auction && rowCategory(auction as never) === "cards";
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 pb-24 lg:pb-10">
      <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <MediaGallery images={auction.images} title={auction.title} />
          

          {inspection && inspection.status !== "failed" && (
            <div className="mt-6">
              <Suspense fallback={null}>
                <InspectionPanel report={inspection} />
              </Suspense>
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-auto lg:pr-1 no-scrollbar">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <StatusBadge auction={auction} size="sm" />
            {isCardListing && <GradeBadge auction={auction} size="md" />}
            <span className="rounded-md bg-surface-2 px-2 py-1">{CONDITION_LABEL[auction.condition] ?? auction.condition}</span>
            {isCardListing && (auction.card_set || auction.card_number) && (
              <span className="rounded-md bg-surface-2 px-2 py-1">{auction.card_set}{auction.card_number ? ` #${auction.card_number}` : ""}</span>
            )}
            {auction.card_language && (
              <span className="rounded-md bg-surface-2 px-2 py-1">{formatLanguageBadge(auction.card_language)}</span>
            )}
            {auction.finish && (
              <span className="rounded-md bg-surface-2 px-2 py-1">{auction.finish}</span>
            )}
            {auction.edition && auction.edition !== "Unlimited" && (
              <span className="rounded-md border border-gold/40 bg-gold/10 px-2 py-1 text-gold">{auction.edition}</span>
            )}
          </div>
          <h1 className="mt-3 font-display text-4xl">{auction.title}</h1>
          {seller && (
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>
                Sold by{" "}
                <SellerHoverCard sellerId={seller.id} preloaded={seller}>
                  <Link
                    to="/seller/$sellerId"
                    params={{ sellerId: seller.username ?? seller.id }}
                    className="text-foreground hover:text-gold underline-offset-4 hover:underline"
                  >
                    {resolvePublicSellerName(seller)}
                  </Link>
                </SellerHoverCard>
              </span>
              <SellerBadgeStack sellerId={seller.id} size="sm" />
              {seller.is_verified_seller && !seller.id && <VerifiedSellerBadge />}
              <SellerRatingInline
                rating={seller.seller_rating}
                count={seller.seller_rating_count}
              />
            </div>
          )}

          {auction.description && <p className="mt-4 text-muted-foreground whitespace-pre-line">{auction.description}</p>}

          {!isCardListing && (() => {
            const row = auction as unknown as { category?: string | null; subcategory?: string | null; attributes?: Record<string, unknown> | null; brand?: string | null };
            const area = rowCategory(row as never);
            const vals = (row.attributes ?? {}) as Record<string, unknown>;
            const rows: { label: string; value: string }[] = [];
            const sub = subcategoryLabel(area, row.subcategory);
            if (sub) rows.push({ label: "Category", value: `${area === "fashion" ? "Fashion" : "Collectibles"} · ${sub}` });
            if (row.brand) rows.push({ label: "Brand", value: row.brand });
            for (const f of attributeFieldsFor(area, row.subcategory)) {
              const v = vals[f.key];
              if (f.type === "boolean") { if (v === true) rows.push({ label: f.label, value: "Yes" }); }
              else if (typeof v === "string" && v) rows.push({ label: f.label, value: v });
            }
            rows.push({ label: "Condition", value: CONDITION_LABEL[auction.condition] ?? auction.condition });
            return (
              <div className="mt-6 rounded-2xl border border-border/60 bg-card/50 p-5">
                <h2 className="font-display text-lg">Item Details</h2>
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  {rows.map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3 border-b border-border/30 py-1.5 sm:border-b-0">
                      <dt className="text-muted-foreground">{r.label}</dt>
                      <dd className="text-right font-medium">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })()}

          {(() => {
            const a = auction as unknown as {
              trading_card_game?: string | null; trading_card_game_other?: string | null;
              card_language?: string | null; card_set?: string | null; card_number?: string | null;
              edition?: string | null; finish?: string | null;
              rarity?: string | null; rarity_other?: string | null;
              grading_status?: string | null; grading_company?: string | null; grade?: string | null;
              grading_cert_number?: string | null; condition?: string | null;
              is_autographed?: boolean | null; autograph_details?: string | null;
              is_serial_numbered?: boolean | null; serial_number?: string | null;
              product_type?: string | null;
            };
            if (a.product_type && a.product_type !== "trading_card") return null;
            const rows: { label: string; value: string }[] = [];
            const gameLabel = a.trading_card_game === "other"
              ? (a.trading_card_game_other || "Other")
              : (a.trading_card_game ? (TCG_GAME_LABEL[a.trading_card_game] ?? a.trading_card_game) : null);
            if (gameLabel) rows.push({ label: "Trading Card Game", value: gameLabel });
            if (a.card_set) rows.push({ label: "Card Set", value: a.card_set });
            if (a.card_number) rows.push({ label: "Card Number", value: a.card_number });
            if (a.card_language) rows.push({ label: "Language", value: a.card_language });
            if (a.edition) rows.push({ label: "Edition", value: a.edition });
            if (a.finish) rows.push({ label: "Finish", value: a.finish });
            const rarityLabel = a.rarity === "other" ? (a.rarity_other || null) : (a.rarity ? (RARITY_LABEL[a.rarity] ?? a.rarity) : null);
            if (rarityLabel) rows.push({ label: "Rarity", value: rarityLabel });

            const status = a.grading_status ?? (a.grading_company && a.grading_company !== "RAW" ? "graded" : "raw");
            if (status === "graded" && a.grading_company && a.grading_company !== "RAW") {
              rows.push({ label: "Grading", value: `${String(a.grading_company).toUpperCase()}${a.grade && a.grade !== "Ungraded" ? ` ${a.grade}` : ""}` });
              if (a.grading_cert_number) rows.push({ label: "Certification #", value: a.grading_cert_number });
            } else if (status === "raw") {
              const cond = a.condition ? (CONDITION_LABEL[a.condition] ?? a.condition) : null;
              rows.push({ label: "Grading", value: cond ? `Raw · ${cond}` : "Raw / Ungraded" });
            }
            if (a.is_autographed) rows.push({ label: "Autographed", value: a.autograph_details ? `Yes — ${a.autograph_details}` : "Yes" });
            if (a.is_serial_numbered && a.serial_number) rows.push({ label: "Serial Number", value: a.serial_number });

            if (rows.length === 0) return null;
            return (
              <div className="mt-6 rounded-2xl border border-border/60 bg-card/50 p-5">
                <h2 className="font-display text-lg">Trading Card Details</h2>
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  {rows.map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3 border-b border-border/30 py-1.5 sm:border-b-0">
                      <dt className="text-muted-foreground">{r.label}</dt>
                      <dd className="text-right font-medium">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })()}


          {auction.grading_cert_number && isApiGrader(String(auction.grading_company ?? "")) && (
            <div className="mt-6 rounded-2xl border border-border/60 bg-card/50 p-5">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                Verify grading certificate
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Seller listed cert <span className="font-mono">{auction.grading_cert_number}</span> ({String(auction.grading_company).toUpperCase()}).
              </p>
              <div className="mt-3">
                <GradingCertBadge
                  company={String(auction.grading_company ?? "")}
                  certNumber={auction.grading_cert_number}
                  verified={(auction as { cert_verified?: boolean | null }).cert_verified ?? false}
                  status={(auction as { cert_verification_status?: string | null }).cert_verification_status ?? null}
                  officialUrl={(auction as { cert_official_url?: string | null }).cert_official_url ?? null}
                  verifiedAt={(auction as { cert_verified_at?: string | null }).cert_verified_at ?? null}
                />
              </div>

              <div className="mt-3">
                <GradingCertLookup
                  compact
                  initialCompany={String(auction.grading_company).toLowerCase() as ApiGrader}
                  initialCert={auction.grading_cert_number}
                />
              </div>

            </div>
          )}

          <div className="mt-6">
            <RegistrySection auctionId={auction.id} />
          </div>



          {(() => {
            const topBidderId = bids?.[0]?.bidder_id;
            let state: "winning" | "outbid" | null = null;
            if (user && !isEnded && auction.status === "active" && topBidderId) {
              if (topBidderId === user.id) state = "winning";
              else if (bids?.some((b) => b.bidder_id === user.id)) state = "outbid";
            }
            return <BidStatusBanner state={state} />;
          })()}

          <div className={cn(
            "mt-4 rounded-2xl border bg-card p-6 gold-ring transition-shadow",
            "border-border/60 shadow-[0_20px_60px_-30px_rgba(139,92,246,0.35)]",
          )}>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {(auction.current_bid_pence ?? 0) > 0 ? tb("currentBid", "Current bid") : tb("startingPrice", "Starting price")}
                </p>
                <p className="font-display text-4xl text-gold-gradient"><Price pence={currentBid} /></p>
                {!isGBP && <CurrencyDisclaimer className="mt-1" />}
                <p className="text-xs text-muted-foreground mt-1">{tb("bids", "{count, plural, one {# bid} other {# bids}}", { count: auction.bid_count })}</p>
                {auction.reserve_price_pence && auction.status === "active" && (
                  <p className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-widest ${
                    (auction.current_bid_pence ?? 0) >= auction.reserve_price_pence
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                      : "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                  }`}>
                    {(auction.current_bid_pence ?? 0) >= auction.reserve_price_pence ? tb("reserveMet", "Reserve met") : tb("reserveNotMet", "Reserve not yet met")}
                  </p>
                )}
                {auction.buy_now_enabled && auction.buy_now_price_pence && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className="uppercase tracking-widest text-[10px]">{tb("buyNow", "Buy It Now")}</span>{" "}
                    <span className="ml-1 font-semibold text-gold">{formatGBP(auction.buy_now_price_pence)}</span>
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{tb("endsIn", "Auction ends in")}</p>
                <Countdown endTime={auction.end_time} className="text-2xl" />
                {auction.status === "active" && autoExtend.enabled && (
                  <div className="mt-2 flex justify-end">
                    <AutoExtendBadge config={autoExtend} />
                  </div>
                )}
              </div>

            </div>

            {auction.status === "reserve_not_met" ? (
              <div className="mt-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-center text-sm text-amber-300">
                <div className="font-display text-lg text-amber-200">{tb("reserveNotMetTitle", "Reserve Not Met")}</div>
                <p className="mt-1 text-amber-300/80">
                  {tb("reserveNotMetBody", "The highest bid did not reach the seller's reserve price. No winner was declared and no order was created.")}
                </p>
              </div>
            ) : isEnded ? (
              <div className="mt-6 space-y-3">
                <div className="rounded-lg bg-surface-2 p-4 text-center text-sm">
                  {auction.status === "sold" && isWinner
                    ? tb("youWon", "🎉 You won this auction!")
                    : auction.status === "sold"
                    ? tb("auctionEndedSold", "This auction has ended and been sold.")
                    : tb("auctionEnded", "This auction has ended.")}
                </div>
                {isWinner && (
                  winnerOrderLoading ? (
                    <Button disabled className="w-full bg-gold-gradient opacity-80">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {tb("loadingOrder", "Loading your order…")}
                    </Button>
                  ) : winnerOrder && winnerOrder.status === "pending_payment" && !winnerOrder.has_active_dispute ? (
                    <Button
                      className="w-full bg-gold-gradient hover:opacity-90"
                      onClick={() => payNow.mutate()}
                      disabled={payNow.isPending}
                    >
                      {payNow.isPending
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {tb("preparingPayment", "Preparing payment…")}</>
                        : <><CreditCard className="h-4 w-4 mr-2" /> {tb("payNow", "Pay now")}</>}
                    </Button>
                  ) : winnerOrder && winnerOrder.has_active_dispute ? (
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-center text-xs text-red-300">
                      {tb("disputeHold", "A dispute is active on this order — payment is on hold.")}
                    </div>
                  ) : winnerOrder ? (
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/orders">{tb("viewOrder", "View order")}</Link>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => payNow.mutate()}
                      disabled={payNow.isPending}
                    >
                      {payNow.isPending
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {tb("preparingPayment", "Preparing payment…")}</>
                        : tb("preparePayment", "Prepare payment")}
                    </Button>
                  )
                )}
              </div>

            ) : !user ? (
              <Button asChild className="mt-6 w-full bg-gold-gradient hover:opacity-90">
                <Link to="/auth" search={{ mode: "signin", redirect: `/auction/${buildAuctionSlug({ id, title: auction.title, grading_company: auction.grading_company, grade: auction.grade })}` } as never}>{tb("signInToBid", "Sign in to bid")}</Link>
              </Button>
            ) : isSeller ? (
              <div className="mt-6 space-y-3">
                <div className="rounded-lg bg-surface-2 p-4 text-center text-sm text-muted-foreground">
                  {tb("ownAuction", "You can't bid on your own auction.")}
                </div>
                {auction.status === "active" && !isEnded && (
                  <Button
                    type="button"
                    onClick={() => setPromoteOpen(true)}
                    className="w-full bg-gold-gradient hover:opacity-90"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {tb("promoteListing", "Promote listing")}
                  </Button>
                )}
                <PromoteListingDialog
                  open={promoteOpen}
                  onOpenChange={setPromoteOpen}
                  auctionId={id}
                  auctionStatus={auction.status}
                  auctionEndTime={auction.end_time}
                />
              </div>
            ) : reservationActive && !reservedByMe ? (
              <div className="mt-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-center text-sm text-amber-300">
                <Lock className="h-4 w-4 inline mr-2" />
                {tb("biddingPaused", "Another buyer is completing a Buy It Now checkout. Bidding is paused for up to 10 minutes.")}
              </div>
            ) : (
              <form
                ref={bidFormRef}
                onSubmit={(e) => {
                  e.preventDefault();
                  const p = Number(bidAmount);
                  if (!Number.isFinite(p) || p <= 0) {
                    toast.error(tb("invalidAmount", "Enter a valid amount"));
                    return;
                  }
                  if (Math.round(p * 100) < minNext) {
                    toast.error(tb("minimumBid", "Minimum bid is {amount}", { amount: formatGBP(minNext) }));
                    return;
                  }
                  track("bid_confirm_open", { auction_id: id, amount_pence: Math.round(p * 100) });
                  setConfirmOpen(true);
                }}
                className="mt-6 space-y-3"
              >
                {buyNowAvailable && auction.buy_now_price_pence && (
                  <Button
                    type="button"
                    onClick={() => setBuyNowOpen(true)}
                    className="w-full bg-gold-gradient hover:opacity-90 h-12 press-tap"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {tb("buyNow", "Buy It Now")} — {formatGBP(auction.buy_now_price_pence)}
                  </Button>
                )}
                {binDisabledByBidding && (
                  <p className="text-[11px] text-muted-foreground text-center">
                    {tb("buyNowUnavailable", "Buy It Now is no longer available because bidding has started.")}
                  </p>
                )}
                <Label htmlFor="bid-amount">{tb("yourBid", "Your bid (min {amount})", { amount: formatGBP(minNext) })}</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input
                      id="bid-amount"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min={(minNext / 100).toFixed(2)}
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder={(minNext / 100).toFixed(2)}
                      className="pl-7"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={bidMutation.isPending} className="bg-gold-gradient hover:opacity-90 press-tap">
                    {bidMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gavel className="h-4 w-4 mr-2" /> {tb("placeBid", "Place bid")}</>}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 5].map((mult) => {
                    const suggested = minNext + auction.bid_increment_pence * (mult - 1);
                    return (
                      <button
                        key={mult}
                        type="button"
                        onClick={() => setBidAmount((suggested / 100).toFixed(2))}
                        className="rounded-md border border-border/60 bg-surface-2 px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors press-tap"
                      >
                        {formatGBP(suggested)}
                      </button>
                    );
                  })}
                </div>
                <Label htmlFor="max-bid-amount" className="text-xs text-muted-foreground">
                  Max bid (optional) — we'll bid automatically for you up to this amount
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="max-bid-amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min={(minNext / 100).toFixed(2)}
                    value={maxBidAmount}
                    onChange={(e) => setMaxBidAmount(e.target.value)}
                    placeholder="Leave blank to bid the exact amount"
                    className="pl-7"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-border/60 press-tap"
                    onClick={() => {
                      watchMutation.mutate();
                      if (!watched) track("add_to_watchlist", { auction_id: id });
                    }}
                    disabled={watchMutation.isPending}
                  >
                    <Heart className={`h-4 w-4 mr-2 ${watched ? "fill-gold text-gold" : ""}`} />
                    {watched ? "Watching" : "Watch"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-border/60 press-tap"
                    onClick={async () => {
                      const url =
                        typeof window !== "undefined"
                          ? `${window.location.origin}/auction/${buildAuctionSlug({ id, title: auction.title, grading_company: auction.grading_company, grade: auction.grade })}`
                          : "";
                      const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }) : null;
                      try {
                        if (nav?.share) {
                          await nav.share({ title: auction.title, url });
                        } else {
                          await navigator.clipboard.writeText(url);
                          toast.success("Link copied");
                        }
                      } catch {
                        /* user cancelled */
                      }
                    }}
                  >
                    <Share2 className="h-4 w-4 mr-2" /> Share
                  </Button>
                </div>
              </form>
            )}
          </div>

          <AuctionCertificateBadge auctionId={id} />

          <ShippingSummary />

          <TrustStrip />

          <ActivitySummary auction={auction} />

          <BidHistory auctionId={id} bidCount={auction.bid_count ?? 0} isEnded={isEnded} />

          <AuctionFaq auction={auction} />
        </div>
      </div>


      <SellerOtherAuctions
        currentId={id}
        sellerId={auction.seller_id}
        sellerName={seller?.display_name}
      />


      <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden />}>
        <SimilarAuctionsWidget currentId={id} reference={auctionToScorable(auction)} />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden />}>
        <RelatedAuctionsWidget currentId={id} reference={auctionToScorable(auction)} preset="set" />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden />}>
        <RelatedAuctionsWidget currentId={id} reference={auctionToScorable(auction)} preset="grade" />
      </Suspense>
      <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden />}>
        <RecentlyViewedRecoWidget excludeId={id} />
      </Suspense>

      <BidConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={auction.title}
        image={auction.images?.[0]}
        bidPence={Math.round((Number(bidAmount) || 0) * 100)}
        currentBidPence={auction.current_bid_pence}
        isPending={bidMutation.isPending}
        onConfirm={() => {
          track("bid_submit", { auction_id: id, amount_pence: Math.round((Number(bidAmount) || 0) * 100) });
          bidMutation.mutate({ pounds: bidAmount, maxPounds: maxBidAmount });
        }}
      />

      {!isEnded && !isSeller && user && auction.status === "active" && !reservedByOther && (
        <>
          {/* Spacer so the fixed mobile bid bar never covers page content, cookie banner, or the assistant widget. */}
          <div aria-hidden className="lg:hidden h-20" style={{ height: "calc(4rem + env(safe-area-inset-bottom))" }} />
          <MobileBidBar
            currentBidPence={currentBid}
            endTime={auction.end_time}
            onBid={() => bidFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
          />
        </>
      )}


      <Dialog open={buyNowOpen} onOpenChange={(o) => !buyNowMutation.isPending && setBuyNowOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Buy It Now</DialogTitle>
            <DialogDescription>
              Purchase <span className="text-foreground font-medium">{auction.title}</span> instantly at the fixed price. Bidding on this auction will be paused while you complete checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-border/60 bg-surface-2 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Item price</span>
                <span className="font-medium">{formatGBP(auction.buy_now_price_pence ?? 0)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Shipping</span>
                <span className="font-medium">
                  {formatGBP(SHIPPING_OPTIONS.find((o) => o.id === buyNowShipping)?.costPence ?? 0)}
                </span>
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t border-border/60">
                <span className="font-medium">Total</span>
                <span className="font-display text-gold">
                  {formatGBP(
                    (auction.buy_now_price_pence ?? 0) +
                      (SHIPPING_OPTIONS.find((o) => o.id === buyNowShipping)?.costPence ?? 0),
                  )}
                </span>
              </div>
            </div>
            <div>
              <Label htmlFor="bin-shipping">Shipping method</Label>
              <select
                id="bin-shipping"
                value={buyNowShipping}
                onChange={(e) => setBuyNowShipping(e.target.value)}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {AVAILABLE_SHIPPING_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label} — {formatGBP(o.costPence)}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-muted-foreground">
              The auction will be reserved for you for 10 minutes while you complete payment. If checkout expires or fails, the reservation is released automatically and bidding resumes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBuyNowOpen(false)} disabled={buyNowMutation.isPending}>
              Cancel
            </Button>
            <Button
              className="bg-gold-gradient hover:opacity-90"
              onClick={() => buyNowMutation.mutate()}
              disabled={buyNowMutation.isPending}
            >
              {buyNowMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reserving…</>
              ) : (
                <><Zap className="h-4 w-4 mr-2" /> Confirm & pay</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

